// Formular einer Custom Activity, dynamisch aus der Typdefinition: Pflichtfelder markiert, Eingabe je Feldtyp.
// Alle Auswahl-, Nutzer- und Kontaktfelder sind Dropdowns (nummeriert, Ziffer wählt), Datum/Uhrzeit mit Picker.
// Pflichtfelder: Marke „Pflicht“ (grün mit Haken, sobald ausgefüllt); ein leer verlassenes Pflichtfeld wird rot,
// die Fehlermeldung darunter kommt beim Speichern-Versuch.
// Speichern = veröffentlichen (Pflichtfelder geprüft), „Als Entwurf“ ohne Prüfung. Gespeichert wird im CRM, nie in
// Close; die Backoffice-Automatik sieht veröffentlichte Einträge aber über die Brücke und reagiert darauf (Status,
// Score, bei Typen mit „automatisch“ in der Beschreibung eine echte Mail bzw. Einladung). Entwürfe sieht sie nicht.
import { Fragment, useState } from "react";
import { ApiFehler, type CustomActivityType, type FeldWert, type Kontakt } from "../../api";
import { feldFehler, istLeer, type FeldDef } from "../../werkzeuge/felder";
import { istHtml, textZuHtml } from "../../werkzeuge/html";
import { modTaste } from "../../werkzeuge/tasten";
import { heute } from "../../werkzeuge/zeit";
import { FeldEingabe } from "../FeldEingabe";
import { Icon } from "../Icon";
import { followUpVorschlaege } from "../inbox/schnellwahl";
import { FOLLOWUP_FELDNAME, FOLLOWUP_WERT, followUpFeld } from "./followUpLogik";
import "../../styles/lead.css";
import { DatumFeld } from "../DatumFeld";

export type Werte = Record<string, FeldWert>;

/** Werte für die API: leere Felder weglassen (bzw. null beim Ändern), Rich Text als HTML. */
export function werteFuerApi(typ: CustomActivityType, werte: Werte, mitNull = false): Record<string, FeldWert> {
  const aus: Record<string, FeldWert> = {};
  for (const f of typ.fields) {
    let w = werte[f.id];
    if (istLeer(w)) {
      if (mitNull) aus[f.id] = null;
      continue;
    }
    if (f.type === "richtextarea" && typeof w === "string" && !istHtml(w)) w = textZuHtml(w);
    aus[f.id] = w;
  }
  return aus;
}

/** Aufeinanderfolgende einzelne Zahlfelder (z. B. Anzahl Mitarbeiter, Beschäftigte, …) bilden ein 2-spaltiges Raster. */
export function feldGruppen(felder: FeldDef[]): FeldDef[][] {
  const gruppen: FeldDef[][] = [];
  for (const f of felder) {
    const zahl = f.type === "number" && !f.accepts_multiple_values;
    const letzte = gruppen[gruppen.length - 1];
    if (zahl && letzte && letzte[0].type === "number" && !letzte[0].accepts_multiple_values) letzte.push(f);
    else gruppen.push([f]);
  }
  return gruppen;
}

/** Löst Speichern bei diesem Typ etwas nach außen aus (Mail, Kalendereinladung)? Steht in der Typbeschreibung. */
export function sendetNachAussen(typ: Pick<CustomActivityType, "description">): boolean {
  return /automatisch/i.test(typ.description ?? "");
}

export function pruefen(typ: CustomActivityType, werte: Werte): Record<string, string> {
  const fehler: Record<string, string> = {};
  for (const f of typ.fields) {
    const text = feldFehler(f as FeldDef, werte[f.id], f.required);
    if (text) fehler[f.id] = text;
  }
  return fehler;
}

interface Props {
  typ: CustomActivityType;
  kontakte: Kontakt[];
  werte: Werte;
  onChange: (w: Werte) => void;
  onSpeichern: (status: "published" | "draft") => Promise<void>;
  onVerwerfen?: () => void;
  /** Beschriftung des Verwerfen-Knopfs (Standard: „Abbrechen“ beim Bearbeiten, sonst „Leeren“) */
  verwerfenText?: string;
  /** Bearbeiten einer vorhandenen Aktivität */
  bearbeiten?: boolean;
  serverFehler?: unknown;
  /** schmale Fläche (Telefon-Feld): ohne Hinweistexte im Fuß */
  schmal?: boolean;
  /** Tastenhinweis im Fuß (Standard: „⌘ Enter speichert“) */
  tastenHinweis?: string;
  /** Beschriftung des Speichern-Knopfs (Standard: „Speichern und senden“ bei Typen, die nach außen senden, sonst „Speichern“) */
  speichernText?: string;
  /** Neuer Eintrag am Lead: bei einer Follow-Up-Auswahl erscheint darunter das (freiwillige) Datumsfeld „Follow-UP Datum“.
   *  Das Datum steht in `werte[FOLLOWUP_WERT]`; geschrieben wird es vom Aufrufer (followUpSchreiben) in das Lead-Feld. */
  followUp?: boolean;
}

/** Freiwilliges Datum „Follow-UP Datum“ direkt unter dem Ergebnis: schnelle Vorschläge (erneuter Klick nimmt zurück) und Kalender. */
function FollowUpDatumFeld({ id, wert, onChange }: { id: string; wert: string; onChange: (tag: string) => void }) {
  const h = heute();
  return (
    <div className="ls-caf__feld ls-caf__followup" data-testid="followup-datum">
      <label className="ls-caf__label" htmlFor={id}>
        {FOLLOWUP_FELDNAME}
      </label>
      <div className="ls-caf__followup-zeile">
        {followUpVorschlaege(h).map((v) => (
          <button
            key={v.label}
            type="button"
            className={`wahlknopf wahlknopf--klein${wert === v.tag ? " wahlknopf--an" : ""}`}
            aria-pressed={wert === v.tag}
            title={`${v.label}, ${v.kurz}`}
            onClick={() => onChange(wert === v.tag ? "" : v.tag)}
          >
            {v.label}
            <span className="ls-caf__followup-kurz">{v.kurz}</span>
          </button>
        ))}
        <DatumFeld zukunft id={id} className="eingabe eingabe--datum eingabe--klein" value={wert} onChange={(e) => onChange(e.target.value)} aria-label={FOLLOWUP_FELDNAME} />
      </div>
    </div>
  );
}

export function CustomActivityFormular({ typ, kontakte, werte, onChange, onSpeichern, onVerwerfen, verwerfenText, bearbeiten = false, serverFehler, schmal = false, tastenHinweis, speichernText, followUp = false }: Props) {
  const [gezeigt, setGezeigt] = useState(false);
  // Felder, die schon einmal verlassen wurden: dort steht ein Fehler sofort (wie Close „… is required“)
  const [beruehrt, setBeruehrt] = useState<Set<string>>(() => new Set());
  const [laeuft, setLaeuft] = useState<"published" | "draft" | null>(null);
  const fehler = pruefen(typ, werte);
  const gruppen = feldGruppen(typ.fields as FeldDef[]);
  const serverFelder = serverFehler instanceof ApiFehler && serverFehler.code === "pflichtfelder_fehlen" ? ((serverFehler.details.fields as string[] | undefined) ?? []) : [];
  const anzahlPflicht = typ.fields.filter((f) => f.required).length;
  const pflichtOffen = typ.fields.filter((f) => f.required && fehler[f.id]).length;
  const sendet = sendetNachAussen(typ);
  const followUpAusloeser = followUp ? followUpFeld(typ.fields as FeldDef[], werte) : null;
  const knopfText = speichernText ?? (sendet && !bearbeiten ? "Speichern und senden" : "Speichern");

  const speichern = async (status: "published" | "draft") => {
    if (laeuft) return;
    if (status === "published" && Object.keys(fehler).length) {
      setGezeigt(true);
      const erstes = typ.fields.find((f) => fehler[f.id]);
      if (erstes) document.getElementById(`ca-${typ.id}-${erstes.id}`)?.focus();
      return;
    }
    setLaeuft(status);
    try {
      await onSpeichern(status);
      setGezeigt(false);
      setBeruehrt(new Set());
    } finally {
      setLaeuft(null);
    }
  };

  const feldZeile = (f: FeldDef & { description?: string | null }) => {
    // Nach dem Verlassen eines Felds nur rot markieren (keine Zusatzzeile, damit nichts verrutscht, während man z. B.
    // auf „Abbrechen“ klickt); der Text darunter erscheint nach dem Speichern-Versuch.
    const text = (gezeigt && fehler[f.id]) || (serverFelder.includes(f.id) && fehler[f.id] ? "Pflichtfeld" : null);
    const rot = !!text || (beruehrt.has(f.id) && !!fehler[f.id]);
    const id = `ca-${typ.id}-${f.id}`;
    const breit = f.type === "textarea" || f.type === "richtextarea";
    const erfuellt = f.required && !fehler[f.id];
    return (
      <div
        key={f.id}
        className={`ls-caf__feld${breit ? " ls-caf__feld--breit" : ""}${rot ? " ls-caf__feld--fehler" : ""}`}
        onBlur={(e) => {
          // Fokus wandert ins Dropdown (eigene Ebene) oder bleibt im Feld: noch nicht verlassen
          const ziel = e.relatedTarget as Element | null;
          if (ziel && (e.currentTarget.contains(ziel) || ziel.closest(".ausklapp"))) return;
          if (!beruehrt.has(f.id)) setBeruehrt((b) => new Set(b).add(f.id));
        }}
      >
        <label className="ls-caf__label" htmlFor={id}>
          {f.name}
          {f.required ? (
            <span className={`ls-caf__pflicht${erfuellt ? " ls-caf__pflicht--ok" : ""}`} title={erfuellt ? "Pflichtfeld, ausgefüllt" : "Pflichtfeld"}>
              {erfuellt ? <Icon name="haken" groesse={10} /> : null}
              Pflicht
            </span>
          ) : null}
        </label>
        <FeldEingabe id={id} def={f as FeldDef} wert={werte[f.id]} kontakte={kontakte} fehler={rot} onChange={(w) => onChange({ ...werte, [f.id]: w })} />
        {f.description ? <div className="ls-caf__hilfe">{f.description}</div> : null}
        {text ? <div className="ls-caf__fehler">{text}</div> : null}
      </div>
    );
  };

  return (
    <form
      className={`ls-caf${schmal ? " ls-caf--schmal" : ""}`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void speichern("published");
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          void speichern("published");
        }
      }}
    >
      {typ.description && sendet ? (
        <p className="hinweis hinweis--warnung ls-caf__warnung" role="note">
          <Icon name="warnung" groesse={14} />
          <span>
            {typ.description}
            {bearbeiten ? null : " Zum Üben „Als Entwurf“ nehmen, Entwürfe lösen nichts aus."}
          </span>
        </p>
      ) : typ.description ? (
        <p className="ls-caf__beschreibung">{typ.description}</p>
      ) : null}
      {typ.fields.length === 0 ? <p className="ls-caf__beschreibung">Dieser Typ hat keine Felder.</p> : null}
      <div className="ls-caf__felder">
        {gruppen.map((gruppe) =>
          gruppe.length > 1 ? (
            <div key={gruppe[0].id} className="ls-caf__zahlen">
              {gruppe.map((f) => feldZeile(f as FeldDef & { description?: string | null }))}
            </div>
          ) : (
            <Fragment key={gruppe[0].id}>
              {feldZeile(gruppe[0] as FeldDef & { description?: string | null })}
              {followUpAusloeser === gruppe[0].id ? (
                <FollowUpDatumFeld
                  id={`ca-${typ.id}-followup`}
                  wert={typeof werte[FOLLOWUP_WERT] === "string" ? (werte[FOLLOWUP_WERT] as string) : ""}
                  onChange={(tag) => onChange({ ...werte, [FOLLOWUP_WERT]: tag })}
                />
              ) : null}
            </Fragment>
          ),
        )}
      </div>
      <div className="ls-caf__fuss">
        <button type="submit" className="knopf knopf--primaer" disabled={laeuft !== null} title={`${knopfText} (${modTaste} Enter)`}>
          {laeuft === "published" ? "Speichert …" : knopfText}
        </button>
        <button type="button" className="knopf" disabled={laeuft !== null} onClick={() => void speichern("draft")} title="Ohne Pflichtfeld-Prüfung als Entwurf ablegen">
          {laeuft === "draft" ? "Speichert …" : "Als Entwurf"}
        </button>
        {onVerwerfen ? (
          <button type="button" className="knopf knopf--leise" disabled={laeuft !== null} onClick={onVerwerfen} title={bearbeiten ? "Änderungen verwerfen" : "Eingaben verwerfen"}>
            {verwerfenText ?? (bearbeiten ? "Abbrechen" : "Leeren")}
          </button>
        ) : null}
        {schmal ? null : (
          <span className="ls-caf__tasten">
            {anzahlPflicht ? (
              <span className={pflichtOffen ? "ls-caf__pflichtstand" : "ls-caf__pflichtstand ls-caf__pflichtstand--ok"}>
                {pflichtOffen ? `${pflichtOffen} von ${anzahlPflicht} Pflichtfeld${anzahlPflicht === 1 ? "" : "ern"} offen` : "Pflichtfelder ausgefüllt"}
              </span>
            ) : null}
            {anzahlPflicht ? " · " : ""}
            {tastenHinweis ?? `${modTaste} Enter speichert`}
          </span>
        )}
      </div>
    </form>
  );
}
